import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, AppShell, CommandPalette, Logo, useCommandPaletteHotkey, useTheme, type CommandItem, type Crumb } from "@sagot/ui";
import { useCallback, useState } from "react";
import { Outlet, useLocation, useMatches, useNavigate } from "react-router";
import { useGuidePaletteItems } from "../features/guide/use-guide-palette-items";
import { useRouteAgentId } from "../session/permissions";
import { useWorkspaceContext } from "../session/workspace-context";
import { ConsoleSidebarFooter, ConsoleSidebarNav, ConsoleTopbar, useSignOut } from "./console-chrome";
import { ImpersonationBanner } from "./impersonation-banner";
import { buildNavModel, navLabel, toCommandItems, type NavModel } from "./nav-model";
import { AgentSwitcher, WorkspaceSwitcher } from "./switchers";
import { useActiveAgent } from "./use-agents";
import { useFieldNavEntries } from "../features/service/field/field-nav";
import { useInstallationsNavLabel } from "../features/service/installations/installations-nav";
import { useConsoleNav } from "./use-console-nav";

/** Route handle: page title (+ optional parent crumb) for routes that aren't nav items. */
export interface ConsoleRouteHandle {
  titleKey: string;
  parent?: { labelKey: string; to: (ws: string) => string };
}

const hasTitle = (handle: unknown): handle is ConsoleRouteHandle => typeof handle === "object" && handle !== null && "titleKey" in handle;

function useCrumbs(nav: NavModel): Crumb[] {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const handle = useMatches()
    .map((m) => m.handle)
    .filter(hasTitle)
    .at(-1);
  const root: Crumb = { label: summary.name, href: consoleRoutes.dashboard(summary.slug) };
  if (handle) {
    const parent: Crumb[] = handle.parent ? [{ label: t(handle.parent.labelKey), href: handle.parent.to(summary.slug) }] : [];
    return [root, ...parent, { label: t(handle.titleKey) }];
  }
  const active = nav.active;
  if (!active) return [root];
  const activeIndex = nav.entries.findIndex((e) => e.kind === "link" && e.id === active.id);
  const section = nav.entries
    .slice(0, activeIndex)
    .reverse()
    .find((e) => e.kind === "section");
  return [root, ...(section ? [{ label: t(section.i18nKey) }] : []), { label: navLabel(active, (key) => t(key)) }];
}

const ACTION = { signOut: "action:sign-out", theme: "action:theme" } as const;

function usePaletteItems(nav: NavModel, agentId: string | null, paletteOpen: boolean): CommandItem[] {
  const { t } = useTranslation();
  const { summary, me } = useWorkspaceContext();
  const { resolved } = useTheme();
  const nextTheme = resolved === "dark" ? "light" : "dark";
  const guide = useGuidePaletteItems(summary.slug, paletteOpen);
  const pages = toCommandItems(nav.links, summary.slug, agentId, (link) => navLabel(link, (key) => t(key)), t("console.palettePages"));
  const workspaces = me.workspaces
    .filter((w) => w.id !== summary.id && w.membership_status === "active")
    .map((w) => ({ id: `ws:${w.id}`, label: w.name, icon: "building" as const, href: consoleRoutes.dashboard(w.slug), group: t("console.paletteWorkspaces") }));
  const actions: CommandItem[] = [
    { id: "roles", label: t("console.paletteRoles"), icon: "shield-check", href: consoleRoutes.roles(summary.slug), group: t("console.paletteActions") },
    { id: "theme", label: t("console.paletteToggleTheme", { theme: t(`theme.${nextTheme}`) }), icon: nextTheme === "dark" ? "moon" : "sun", href: ACTION.theme, group: t("console.paletteActions") },
    { id: "sign-out", label: t("console.paletteSignOut"), icon: "log-out", href: ACTION.signOut, group: t("console.paletteActions") },
  ];
  return [...pages, ...workspaces, ...actions, ...guide];
}

/** Workspace console shell (mockups `data-shell="tenant"`), wired to /v1/me, /v1/nav, notifications. */
export function ConsoleLayout() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const location = useLocation();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const { resolved, setPreference } = useTheme();
  const routeAgentId = useRouteAgentId();
  // Workspace pages (e.g. the agents list) have no agent in the URL: agent links use the switcher's agent.
  const { active: activeAgent } = useActiveAgent(routeAgentId);
  const agentId = routeAgentId ?? activeAgent?.id ?? null;
  const navQuery = useConsoleNav(agentId);
  const navEntries = useInstallationsNavLabel(useFieldNavEntries(navQuery.entries));
  const nav = buildNavModel(navEntries, summary.slug, agentId, location.pathname);
  const crumbs = useCrumbs(nav);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const items = usePaletteItems(nav, agentId, paletteOpen);
  const openPalette = useCallback(() => {
    setPaletteOpen(true);
  }, []);
  useCommandPaletteHotkey(openPalette);

  const onSelect = (item: CommandItem) => {
    setPaletteOpen(false);
    if (item.href === ACTION.signOut) void signOut();
    else if (item.href === ACTION.theme) setPreference(resolved === "dark" ? "light" : "dark");
    else void navigate(item.href);
  };

  return (
    <AppShell
      locationKey={location.pathname}
      labels={{ skipToContent: t("shell.skipToContent"), closeMenu: t("shell.closeMenu"), sidebar: t("shell.sidebar"), mainNav: t("shell.mainNav") }}
      brand={
        <AppLink href={consoleRoutes.dashboard(summary.slug)} aria-label={t("nav.dashboard")}>
          <Logo tone="light" size={26} />
        </AppLink>
      }
      sidebarTop={
        <>
          <WorkspaceSwitcher />
          <AgentSwitcher agentId={routeAgentId} />
        </>
      }
      nav={<ConsoleSidebarNav entries={nav.entries} isPending={navQuery.isPending} activeId={nav.active?.id} agentId={agentId} />}
      sidebarFooter={<ConsoleSidebarFooter />}
      topbar={<ConsoleTopbar crumbs={crumbs} onOpenPalette={openPalette} />}
    >
      <ImpersonationBanner />
      <Outlet />
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        items={items}
        onSelect={onSelect}
        labels={{ title: t("palette.title"), placeholder: t("palette.placeholder"), empty: t("palette.empty"), navigate: t("palette.navigate"), open: t("palette.open") }}
      />
    </AppShell>
  );
}
